import React from "react";
import { Sparkles } from "lucide-react";

const RADIUS = 52;
const STROKE = 10;
const SIZE = 140;
const CX = SIZE / 2;
const CY = SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const RADIUS_LG = 80;
const STROKE_LG = 14;
const SIZE_LG = 210;
const CX_LG = SIZE_LG / 2;
const CY_LG = SIZE_LG / 2;
const CIRCUMFERENCE_LG = 2 * Math.PI * RADIUS_LG;

function colorForPct(pct) {
  if (pct >= 95) return "#22C55E";   // grøn
  if (pct >= 75) return "#84CC16";   // lys grøn
  if (pct >= 50) return "#EAB308";   // gul
  if (pct >= 25) return "#F97316";   // orange
  return "#EF4444";                  // rød
}

// Beregn x,y for en vinkel på ringen (startvinkel = 6 o'clock = -90 deg offset af SVG)
function dotPosition(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function GoalDots({ percentage, goal1Pct, goal2Pct, reachedGoal2, large }) {
  const r = large ? RADIUS_LG : RADIUS;
  const cx = large ? CX_LG : CX;
  const cy = large ? CY_LG : CY;
  // Ringen starter kl. 12 (top), går med uret. pct 0 = top, pct 100 = rundt om.
  const angle1 = (goal1Pct / 100) * 360;
  const angle2 = (goal2Pct / 100) * 360;
  const dot1 = dotPosition(cx, cy, r, angle1);
  const dot2 = dotPosition(cx, cy, r, angle2);
  const dotR = large ? 5 : 4;

  const dot1Color = percentage >= goal1Pct ? "#22C55E" : percentage >= goal1Pct / 2 ? "#EAB308" : "#3F3F46";
  const dot2Color = reachedGoal2 ? "#22C55E" : "#3F3F46";

  return (
    <>
      {/* Mål 1 dot */}
      <circle cx={dot1.x} cy={dot1.y} r={dotR} fill={dot1Color} stroke="hsl(var(--card))" strokeWidth="1.5" />
      {/* Mål 2 dot — glød kun når nået */}
      <circle cx={dot2.x} cy={dot2.y} r={dotR} fill={dot2Color} stroke="hsl(var(--card))" strokeWidth="1.5"
        style={reachedGoal2 ? { filter: "drop-shadow(0 0 4px #22C55E)" } : {}}
      />
    </>
  );
}

function SimpleRing({ percentage, color, large, glowing }) {
  const r = large ? RADIUS_LG : RADIUS;
  const stroke = large ? STROKE_LG : STROKE;
  const cx = large ? CX_LG : CX;
  const cy = large ? CY_LG : CY;
  const circ = large ? CIRCUMFERENCE_LG : CIRCUMFERENCE;
  const clampedPct = Math.min(percentage, 100);
  const dash = (clampedPct / 100) * circ;
  const gap = circ - dash;
  const offset = circ * 0.25;

  const filterId = large ? "kpi-glow-lg" : (glowing ? "kpi-glow-sm" : null);

  return (
    <>
      {(large || glowing) && (
        <defs>
          <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation={large ? "4" : "3"} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        filter={filterId ? `url(#${filterId})` : undefined}
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
    </>
  );
}

function TrendBadge({ current, previous }) {
  if (current == null || previous == null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return null;
  const up = diff > 0;
  return (
    <span
      className="text-[13px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: up ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)", color: up ? "#22C55E" : "#EF4444" }}
    >
      {up ? "↑" : "↓"} {Math.abs(Math.round(diff))}
    </span>
  );
}

export function KPICircle({
  mode = "simple",
  label,
  centerValue,
  centerUnit,
  percentage,
  subtexts = [],         // array of strings shown below circle
  trendCurrent,          // numeric value for trend
  trendPrevious,         // numeric value for trend
  large = false,         // larger circle for "samlet KPI"
  // Revenue-mode props
  months_elapsed,
  total_months = 6,
  // Goal-mode props — viser dots på ringen for 1. og 2. mål
  goalMode = false,      // aktiver goal-dots
  goal1Pct,              // 0-100: hvor på ringen 1. mål ligger
  goal2Pct,              // 0-100: hvor på ringen 2. mål ligger
  reachedGoal1 = false,
  reachedGoal2 = false,
  goalLabel,             // tekst under label, fx "Mål: 85%"
}) {
  const sz = large ? SIZE_LG : SIZE;
  const cx = large ? CX_LG : CX;
  const cy = large ? CY_LG : CY;
  const pct = percentage ?? 0;

  let color;
  if (mode === "placeholder") {
    color = "#3F3F46";
  } else if (mode === "revenue") {
    const timeProgress = total_months > 0 ? months_elapsed / total_months : 0;
    const expected = timeProgress * 100;
    const actual = pct;
    const deviation = expected > 0 ? ((actual - expected) / expected) * 100 : actual > 0 ? 100 : 0;
    if (deviation >= 5) color = "#22C55E";
    else if (deviation >= -5) color = "#EAB308";
    else color = "#EF4444";
  } else if (goalMode) {
    // Grøn ved goal2, simpel grøn ved goal1, gul halvvejs, ellers rød/orange
    if (reachedGoal2) color = "#22C55E";
    else if (reachedGoal1) color = "#22C55E";
    else if (goal1Pct && pct >= goal1Pct / 2) color = "#EAB308";
    else color = "#EF4444";
  } else {
    color = colorForPct(pct);
  }

  const glowing = goalMode && reachedGoal2;
  const showStars = (mode !== "placeholder" && pct >= 95) || (goalMode && reachedGoal2);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative">
        <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`}>
          <SimpleRing percentage={mode === "placeholder" ? 0 : pct} color={color} large={large} glowing={glowing} />
          {goalMode && goal1Pct != null && goal2Pct != null && (
            <GoalDots
              percentage={pct}
              goal1Pct={goal1Pct}
              goal2Pct={goal2Pct}
              reachedGoal2={reachedGoal2}
              large={large}
            />
          )}
          <text
            x={cx} y={cy - (large ? 12 : 8)}
            textAnchor="middle"
            fill={mode === "placeholder" ? "#52525B" : "hsl(var(--foreground))"}
            fontSize={large ? "34" : "20"}
            fontWeight="800"
            fontFamily="Manrope, sans-serif"
          >
            {mode === "placeholder" ? "—" : centerValue}
          </text>
          {/* Mål-status tekst inde i cirklen */}
          {goalMode && mode !== "placeholder" && (
            <text
              x={cx} y={cy + (large ? 16 : 10)}
              textAnchor="middle"
              fill={reachedGoal2 ? "#22C55E" : reachedGoal1 ? "#86EFAC" : "#71717A"}
              fontSize={large ? "11" : "9"}
              fontWeight="600"
              fontFamily="Manrope, sans-serif"
            >
              {reachedGoal2 ? "Opnået" : reachedGoal1 ? "Opnået" : ""}
            </text>
          )}
        </svg>
        {(trendCurrent != null && trendPrevious != null) && (
          <div className="absolute -top-1 -right-1">
            <TrendBadge current={trendCurrent} previous={trendPrevious} />
          </div>
        )}
        {showStars && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex gap-0.5 pointer-events-none">
            <Sparkles className="w-3 h-3 text-amber-300 animate-pulse-glow" style={{ animationDelay: "0s" }} />
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse-glow" style={{ animationDelay: "0.2s" }} />
            <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse-glow" style={{ animationDelay: "0.4s" }} />
          </div>
        )}
      </div>
      <span className={`font-semibold text-foreground/70 text-center leading-tight ${large ? "text-sm" : "text-[13px]"}`}>{label}</span>
      {goalLabel && (
        <span className="text-[12px] text-muted-foreground text-center leading-tight">{goalLabel}</span>
      )}
    </div>
  );
}
